import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CommonMessages, NewPageWrapper, RichTable, useTitle, getApiConfiguration } from '../../common';
import { CreateCustomRoleDto, PermissionDto, RoleDto, RolesApi } from '../../api-client';
import { useGetBuiltInRoles } from './hooks/useGetBuiltInRoles';
import { ErrorResponse } from '../../common/types/errorRespose';
import { FuseOptions } from 'fuse.js';
import { useSearch } from '../../common/hooks/useSearch/useSearch';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { CellProps } from './BuiltInRoles.types';
import { RolesListRow } from './ui/RolesListRow/RolesListRow';
import { useGetPermissions } from '../Permissions/hooks/useGetPermissions';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { Button, Flexbox } from '@hiiretail/synergy-ui';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { CreateCustomRoleModal } from '../CustomRoles/CustomRolesList/ui/CreateCustomRoleModal/CreateCustomRoleModal';
import { useCreateCustomRole } from '../CustomRoles/hooks/useCreateCustomRole';
import { useQueryClient } from 'react-query';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { useHistory, useRouteMatch } from 'react-router-dom';

export const BuiltInRoles: React.FC = () => {
  const intl = useIntl();
  useTitle(intl.formatMessage(CommonMessages.builtInRoles));
  const [selectedRole, setSelectedRole] = useState<CreateCustomRoleDto | null>(null);
  const [isCreateCustomRoleModalOpen, setIsCreateCustomRoleModalOpen] = useState(false);
  const builtInRolesApi = new RolesApi(getApiConfiguration());
  const history = useHistory();
  const { url } = useRouteMatch();
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationsContext);

  const toggleCreateCustomRoleModalOpen = () => {
    setIsCreateCustomRoleModalOpen((prevState) => !prevState);
  };

  const onCreateCustomRoleClose = () => {
    setSelectedRole(null);
    toggleCreateCustomRoleModalOpen();
  };

  const adaptBuiltInToCustom = (builtInRole: RoleDto): CreateCustomRoleDto => {
    const { id, name, permissions } = builtInRole;

    const adaptedPermissions = permissions.map((p) => ({
      id: p,
    }));

    return {
      id,
      name,
      permissions: adaptedPermissions,
    };
  };

  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(
      <>
        <Flexbox item>
          <RefreshButton onClick={refetchBuiltInRoles} permission="iam.role.list" />
        </Flexbox>
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: builtInRoles,
    isFetching: isLoading,
    error: fetchError,
    refetch: refetchBuiltInRoles,
  } = useGetBuiltInRoles(builtInRolesApi, () => void 0);

  const { data: permissions, isFetching: isLoadingPermissions } = useGetPermissions();

  const searchOptions: FuseOptions<RoleDto> = useMemo(
    () => ({
      keys: ['id', 'name'],
      threshold: 0.1,
      caseSensitive: false,
      findAllMatches: true,
    }),
    [],
  );

  const {
    mutate: createCustomRole,
    isLoading: isCreating,
    error: createError,
  } = useCreateCustomRole({
    onSuccess: ({ data: { id } }) => {
      showNotification(`Custom role ${id} created successfully`);
      queryClient.invalidateQueries('custom-roles').then();
      toggleCreateCustomRoleModalOpen();
      history.push(`custom-roles/${id}`);
    },
  });

  const { filteredData: filteredBuiltInRoles, onSearchChange } = useSearch(
    builtInRoles as RoleDto[],
    searchOptions,
  );

  const tableColumns = useMemo(
    () => [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Name', accessor: 'name' },
      {
        Header: 'Permissions',
        accessor: 'permissions',
        Cell: ({ row: { original } }: CellProps) => (
          <RolesListRow original={original} permissions={permissions as PermissionDto[]} />
        ),
      },
      {
        Header: 'Actions',
        width: 50,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: RoleDto;
          };
        }) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRole(adaptBuiltInToCustom(original));
              toggleCreateCustomRoleModalOpen();
            }}
            variant="secondary"
          >
            Clone
          </Button>
        ),
      },
    ],
    [permissions],
  );

  return (
    <NewPageWrapper>
      {isCreateCustomRoleModalOpen && (
        <CreateCustomRoleModal
          onClose={onCreateCustomRoleClose}
          isOpen={isCreateCustomRoleModalOpen}
          isCreating={isCreating}
          error={createError}
          createCustomRole={createCustomRole}
          selectedRole={selectedRole}
        />
      )}
      <RichTable
        id="built-in-roles"
        title="Browse built-in roles"
        columns={tableColumns}
        data={filteredBuiltInRoles as unknown as TableData[]}
        isLoading={isLoading || isLoadingPermissions}
        fetchError={fetchError as ErrorResponse | null}
        refetch={refetchBuiltInRoles}
        errorSubject="built-in roles"
        searchFieldLabel="Filter by id or name"
        onSearchChange={onSearchChange}
        onRowClick={(row) => history.push(`${url}/${row.id}`)}
        permissions={{
          list: 'iam.role.list',
          create: 'iam.role.create',
        }}
      />
    </NewPageWrapper>
  );
};
